import { useMemo } from 'react';
import { CriterionOption } from '@app/domain/model/eshop/transport/CriterionOption';
import { CriterionSubgroupCode } from '@app/domain/model/eshop/transport/CriterionSubgroupCode';
import { ProductDataCriterion } from '@app/domain/model/eshop/transport/ProductDataCriterion';

const criterionCodeOrder = (code: string): number => {
    const parsed = parseInt(code.replace(/\D/g, ''), 10);

    return Number.isNaN(parsed) ? Number.MIN_SAFE_INTEGER : parsed;
};

const byCriterionCodeAsc = (a: CriterionOption, b: CriterionOption): number =>
    criterionCodeOrder(a.code) - criterionCodeOrder(b.code);

export function useCriterionSubgroups(criterions: ProductDataCriterion[]) {
    const subgroups = useMemo(() => {
        return {
            ownerAge: criterions.find(criterion => criterion.criterionSubgroupCode === CriterionSubgroupCode.OwnerAge),
            experienceOtherDrivers: criterions.find(
                criterion => criterion.criterionSubgroupCode === CriterionSubgroupCode.ExperienceOtherDrivers,
            ),
        };
    }, [criterions]);

    const defaults = useMemo(
        () => ({
            ownerAge: subgroups.ownerAge?.criterionOptions.find(
                option => option.code === subgroups.ownerAge?.defaultCriterionCode,
            ),
            experienceOtherDrivers: subgroups.experienceOtherDrivers?.criterionOptions.find(
                option => option.code === subgroups.experienceOtherDrivers?.defaultCriterionCode,
            ),
        }),
        [subgroups],
    );

    const options = useMemo(
        () => ({
            ownerAge:
                subgroups.ownerAge?.criterionOptions.map(criterion => ({
                    label: criterion.name,
                    value: criterion.code,
                })) || [],
            experienceOtherDrivers:
                [...(subgroups.experienceOtherDrivers?.criterionOptions || [])]
                    .sort(byCriterionCodeAsc)
                    .map(criterion => ({
                        label: criterion.name,
                        value: criterion.code,
                    })) || [],
        }),
        [subgroups],
    );

    const findOption = (subgroupCode: CriterionSubgroupCode, code: string): CriterionOption | null => {
        const subgroup =
            subgroupCode === CriterionSubgroupCode.OwnerAge ? subgroups.ownerAge : subgroups.experienceOtherDrivers;

        return subgroup?.criterionOptions.find(option => option.code === code) ?? null;
    };

    return {
        subgroups,
        defaults,
        options,
        findOption,
    };
}
